import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Compass, Search, Users, Settings as SettingsIcon, Sparkles, Building2, MapPin,
  TrendingUp, Phone, Globe, CheckCircle2, AlertTriangle, RefreshCw, Plug, Gauge, Target, ChevronRight,
} from "lucide-react";

const money = (v) => (v == null || v === "" ? "—" : typeof v === "number" ? `$${Math.round(v).toLocaleString()}` : v);

function scoreColor(s) {
  if (s >= 90) return "bg-seagreen/15 text-seagreen border-seagreen/30";
  if (s >= 75) return "bg-atlantic/10 text-atlantic border-atlantic/30";
  if (s >= 60) return "bg-seaamber/15 text-seaamber border-seaamber/40";
  return "bg-cloud text-mutedsea border-border";
}

const VIEWS = [
  ["today", "Today", Compass],
  ["find", "Find Prospects", Search],
  ["mine", "My Prospects", Users],
  ["management", "Management", Gauge],
  ["settings", "Settings", SettingsIcon],
];

export default function EnduranceIntelligence() {
  const { can } = useAuth();
  const [view, setView] = useState("today");
  return (
    <div className="animate-fade-in" data-testid="endurance-intelligence">
      <div className="flex items-center gap-2.5 mb-1">
        <Sparkles className="h-6 w-6 text-atlantic" />
        <h2 className="font-serif text-3xl text-navy">Endurance Intelligence</h2>
      </div>
      <p className="text-sm text-mutedsea mb-4">Your best opportunities, prioritized for today.</p>
      <div className="flex gap-1 border-b border-border mb-5 overflow-x-auto">
        {VIEWS.filter((v) => (v[0] !== "settings" && v[0] !== "management") || can.write).map(([key, label, Icon]) => (
          <button key={key} data-testid={`intel-tab-${key}`} onClick={() => setView(key)}
            className={`flex items-center gap-1.5 px-3.5 py-2 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${view === key ? "border-atlantic text-navy font-medium" : "border-transparent text-mutedsea hover:text-slate"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      {view === "today" && <TodayView onOpen={() => setView("mine")} />}
      {view === "find" && <FindProspects />}
      {view === "mine" && <MyProspects />}
      {view === "management" && can.write && <ManagementView />}
      {view === "settings" && can.write && <IntelSettings />}
    </div>
  );
}

/* ---------------------------- MANAGEMENT ---------------------------- */
function ManagementView() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/intelligence/management/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-mutedsea text-sm py-10 text-center">Loading…</div>;
  const C = d.cards;
  const cards = [["new_prospects", "New Prospects"], ["high_priority", "High-Priority"], ["contacted", "Contacted"], ["opportunities_created", "Opportunities"], ["bound", "Bound"]];
  return (
    <div className="space-y-4" data-testid="intel-management">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {cards.map(([k, l]) => <div key={k} className="bg-white border border-border rounded-sm p-4" data-testid={`mgmt-${k}`}><div className="font-serif text-3xl text-navy">{C[k] ?? 0}</div><div className="text-xs text-mutedsea">{l}</div></div>)}
        <div className="bg-white border border-border rounded-sm p-4"><div className="font-serif text-2xl text-navy">{money(C.estimated_pipeline)}</div><div className="text-xs text-mutedsea">Est. Pipeline</div></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white border border-border rounded-sm p-4"><div className="font-serif text-base text-navy mb-2">Score → Outcome</div>
          <div className="text-sm text-slate space-y-1">
            <div className="flex justify-between"><span className="text-mutedsea">Conversion rate</span><b>{d.score_to_outcome.conversion_rate}%</b></div>
            <div className="flex justify-between"><span className="text-mutedsea">Avg score (converted)</span><b>{d.score_to_outcome.converted_avg_score}</b></div>
            <div className="flex justify-between"><span className="text-mutedsea">Avg score (not yet)</span><b>{d.score_to_outcome.not_converted_avg_score}</b></div>
          </div>
        </div>
        {[["top_industries", "Top Industries"], ["top_producers", "Top Producers"]].map(([k, l]) => (
          <div key={k} className="bg-white border border-border rounded-sm p-4"><div className="font-serif text-base text-navy mb-2">{l}</div>
            <div className="text-sm space-y-1">{(d[k] || []).map(([name, n], i) => <div key={i} className="flex justify-between"><span className="text-mutedsea truncate">{name}</span><b className="text-navy">{n}</b></div>)}{(d[k] || []).length === 0 && <div className="text-xs text-mutedsea">No data yet.</div>}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-border rounded-sm p-4"><div className="font-serif text-base text-navy mb-2 flex items-center gap-2"><Gauge className="h-4 w-4 text-atlantic" /> Apollo Usage (30 days)</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">{Object.entries(d.apollo_usage).map(([k, v]) => <div key={k}><div className="font-serif text-2xl text-navy">{v}</div><div className="text-xs text-mutedsea">{k.replace(/_/g, " ")}</div></div>)}</div>
      </div>
    </div>
  );
}

/* ------------------------------- TODAY ------------------------------- */
const CARD_DEFS = [
  ["high_priority", "High-Priority Prospects", Target],
  ["need_followup", "Need Follow-Up", RefreshCw],
  ["new_replies", "New Replies", CheckCircle2],
  ["approaching_renewals", "Approaching Known Renewals", TrendingUp],
  ["new_signals", "New Intelligence Signals", Sparkles],
];

function TodayView() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const load = useCallback(async () => {
    setBusy(true);
    try { setData((await api.get("/intelligence/today")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (busy) return <div className="text-mutedsea text-sm py-10 text-center">Loading your priorities…</div>;
  const cards = data?.cards || {};
  const top = data?.top_opportunities || [];
  return (
    <div className="space-y-5" data-testid="intel-today">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {CARD_DEFS.map(([key, label, Icon]) => (
          <div key={key} className="bg-white border border-border rounded-sm p-4" data-testid={`intel-card-${key}`}>
            <Icon className="h-4 w-4 text-atlantic mb-1.5" />
            <div className="font-serif text-3xl text-navy">{cards[key] ?? 0}</div>
            <div className="text-xs text-mutedsea mt-0.5">{label}</div>
          </div>
        ))}
      </div>
      <div className="text-xs uppercase tracking-wider text-mutedsea font-medium">Top Opportunities</div>
      {top.length === 0 ? (
        <div className="bg-white border border-border rounded-sm p-10 text-center text-mutedsea">
          No prospects yet. Use <b>Find Prospects</b> to discover commercial companies.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {top.map((p) => <OpportunityCard key={p.party_id} p={p} />)}
        </div>
      )}
    </div>
  );
}

function OpportunityCard({ p }) {
  return (
    <div className="bg-white border border-border rounded-sm p-4 hover:shadow-md transition-shadow" data-testid={`intel-opp-${p.party_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-serif text-lg text-navy leading-tight">{p.name}</div>
          <div className="text-xs text-mutedsea mt-0.5 flex items-center gap-2 flex-wrap">
            {p.industry && <span>{p.industry}</span>}
            {(p.city || p.state) && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{[p.city, p.state].filter(Boolean).join(", ")}</span>}
            {p.employee_count != null && <span>{p.employee_count} employees</span>}
            {p.revenue_printed && <span>Est. {p.revenue_printed}</span>}
          </div>
        </div>
        <Badge variant="outline" className={`${scoreColor(p.score)} font-medium whitespace-nowrap`}>{p.score} · {p.priority_label}</Badge>
      </div>
      {p.why?.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium text-slate mb-1">Why this matters</div>
          <ul className="text-xs text-mutedsea space-y-0.5">
            {p.why.map((w, i) => <li key={i} className="flex gap-1.5"><ChevronRight className="h-3 w-3 mt-0.5 text-atlantic shrink-0" />{w}</li>)}
          </ul>
        </div>
      )}
      {p.suggested_lines?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {p.suggested_lines.slice(0, 5).map((l) => <Badge key={l} variant="outline" className="bg-cloud text-mutedsea border-border text-[10px]">{l}</Badge>)}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <a href={`/clients/${p.party_id}`} className="text-xs text-atlantic hover:text-navy flex items-center gap-1" data-testid={`intel-open-${p.party_id}`}>View record <ChevronRight className="h-3 w-3" /></a>
      </div>
    </div>
  );
}

/* --------------------------- FIND PROSPECTS --------------------------- */
function FindProspects() {
  const [f, setF] = useState({ location: "", state: "", employees: "", keywords: "", name: "", domain: "" });
  const [rows, setRows] = useState(null);
  const [pag, setPag] = useState({});
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState({});
  const [presets, setPresets] = useState([]);
  const [importing, setImporting] = useState(false);

  useEffect(() => { api.get("/intelligence/presets").then((r) => setPresets(r.data.presets || [])).catch(() => {}); }, []);

  const buildFilters = (extra = {}) => {
    const filters = {};
    if (f.location) filters["organization_locations[]"] = [f.location];
    if (f.state) filters["organization_locations[]"] = [...(filters["organization_locations[]"] || []), f.state];
    if (f.employees) filters["organization_num_employees_ranges[]"] = [f.employees];
    if (f.keywords) filters["q_organization_keyword_tags[]"] = [f.keywords];
    if (f.name) filters["q_organization_name"] = f.name;
    if (f.domain) filters["q_organization_domains_list[]"] = [f.domain];
    return { ...filters, ...extra };
  };

  const search = async (p = 1, presetFilters = null) => {
    setBusy(true); setSel({});
    try {
      const filters = presetFilters ? { ...buildFilters(), ...presetFilters } : buildFilters();
      const { data } = await api.post("/intelligence/search/organizations", { filters, page: p, per_page: 25 });
      setRows(data.organizations); setPag(data.pagination || {}); setPage(p);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setRows([]); }
    finally { setBusy(false); }
  };

  const applyPreset = (preset) => {
    setF((s) => ({ ...s, keywords: preset.keywords || s.keywords }));
    const extra = { ...(preset.filters || {}) };
    if (preset.keywords) extra["q_organization_keyword_tags[]"] = [preset.keywords];
    search(1, extra);
  };

  const addOne = async (org) => {
    try {
      const { data } = await api.post("/intelligence/prospects/import", { organization: org });
      if (data.result === "created") { toast.success(`Added ${org.name} — score ${data.score} (${data.priority_label})`); markStatus(org, "Existing Prospect", data.party_id); }
      else if (data.result === "exists") { toast.info(`${org.name} already exists in Endurance.`); markStatus(org, data.status, data.party_id); }
      else if (data.result === "possible_match") { toast.warning(`Possible existing match for ${org.name} — review before importing.`); markStatus(org, "Possible Match", data.party_id); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markStatus = (org, status, party_id) => setRows((rs) => rs.map((r) => r.apollo_org_id === org.apollo_org_id ? { ...r, crm_status: status, crm_party_id: party_id } : r));

  const selected = (rows || []).filter((r) => sel[r.apollo_org_id]);
  const bulkImport = async () => {
    if (selected.length === 0) return;
    setImporting(true);
    try {
      const { data } = await api.post("/intelligence/prospects/import-bulk", { organizations: selected });
      toast.success(`${data.created} new · ${data.exists} existing · ${data.possible_match} need review`);
      setSel({});
      data.results.forEach((r) => { const org = selected.find((s) => s.name === r.org_name); if (org) markStatus(org, r.result === "created" ? "Existing Prospect" : r.status || "Possible Match", r.party_id); });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="intel-find">
      <div className="bg-white border border-border rounded-sm p-4">
        <div className="font-serif text-lg text-navy mb-3 flex items-center gap-2"><Building2 className="h-4 w-4 text-atlantic" /> Find Commercial Prospects</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[["location", "Location (city/region)"], ["state", "State"], ["employees", "Employees (e.g. 50,250)"], ["keywords", "Keywords / industry"], ["name", "Company name"], ["domain", "Website / domain"]].map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs mb-1">{label}</Label>
              <Input data-testid={`intel-search-${k}`} value={f[k]} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && search(1)} className="bg-white h-9 text-sm" />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {presets.map((p) => (
            <button key={p.key} data-testid={`intel-preset-${p.key}`} onClick={() => applyPreset(p)}
              className="px-2.5 py-1 rounded-full border border-border bg-cloud text-xs text-mutedsea hover:border-atlantic hover:text-navy transition-colors">{p.label}</button>
          ))}
        </div>
        <div className="flex justify-end mt-3">
          <Button data-testid="intel-search-btn" onClick={() => search(1)} disabled={busy} className="bg-navy hover:bg-atlantic text-white">
            <Search className="h-4 w-4 mr-1.5" /> {busy ? "Searching…" : "Search Apollo"}
          </Button>
        </div>
      </div>

      {rows && (
        <div className="bg-white border border-border rounded-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-cloud/50">
            <div className="text-sm text-slate">{pag.total_entries != null ? `${pag.total_entries.toLocaleString()} companies` : `${rows.length} results`}{selected.length > 0 && ` · ${selected.length} selected`}</div>
            {selected.length > 0 && <Button size="sm" data-testid="intel-bulk-import" onClick={bulkImport} disabled={importing} className="bg-navy hover:bg-atlantic text-white h-8">Add {selected.length} Selected</Button>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cloud text-mutedsea text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 w-8"></th>
                  <th className="text-left px-3 py-2">Company</th>
                  <th className="text-left px-3 py-2">Industry / Fit</th>
                  <th className="text-left px-3 py-2">Est. Revenue</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-mutedsea">No companies matched. Broaden your filters.</td></tr>}
                {rows.map((o) => {
                  const isNew = o.crm_status === "New";
                  return (
                    <tr key={o.apollo_org_id} className="border-t border-border hover:bg-cloud/40" data-testid={`intel-result-${o.apollo_org_id}`}>
                      <td className="px-3 py-2">{isNew && <input type="checkbox" checked={!!sel[o.apollo_org_id]} onChange={(e) => setSel((s) => ({ ...s, [o.apollo_org_id]: e.target.checked }))} data-testid={`intel-select-${o.apollo_org_id}`} />}</td>
                      <td className="px-3 py-2">
                        <div className="text-navy font-medium">{o.name}</div>
                        <div className="text-xs text-mutedsea flex items-center gap-2">{o.website && <span className="flex items-center gap-1"><Globe className="h-3 w-3" />{o.domain || o.website}</span>}{o.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{o.phone}</span>}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-slate">{o.industry_display || "—"}</div>
                        <Badge variant="outline" className={`text-[10px] ${o.fit_ratio >= 1 ? "bg-seagreen/15 text-seagreen border-seagreen/30" : o.fit_ratio >= 0.5 ? "bg-seaamber/15 text-seaamber border-seaamber/40" : "bg-cloud text-mutedsea border-border"}`}>{o.fit_label}</Badge>
                      </td>
                      <td className="px-3 py-2 text-slate">{money(o.revenue)}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={o.crm_status === "New" ? "bg-cloud text-mutedsea border-border" : o.crm_status === "Existing Client" ? "bg-seagreen/15 text-seagreen border-seagreen/30" : o.crm_status === "Possible Match" ? "bg-seaamber/15 text-seaamber border-seaamber/40" : "bg-atlantic/10 text-atlantic border-atlantic/30"}>{o.crm_status}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        {isNew
                          ? <Button size="sm" variant="outline" data-testid={`intel-add-${o.apollo_org_id}`} onClick={() => addOne(o)} className="h-7 text-xs">Add Prospect</Button>
                          : o.crm_party_id ? <a href={`/clients/${o.crm_party_id}`} className="text-xs text-atlantic hover:text-navy">View record</a> : <span className="text-xs text-mutedsea">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pag.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-border">
              <Button size="sm" variant="outline" data-testid="intel-prev-page" disabled={page <= 1 || busy} onClick={() => search(page - 1)} className="h-8">Previous</Button>
              <span className="text-xs text-mutedsea">Page {page} of {Math.min(pag.total_pages, 200)}</span>
              <Button size="sm" variant="outline" disabled={page >= pag.total_pages || busy} onClick={() => search(page + 1)} className="h-8" data-testid="intel-next-page">Next</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------------------- MY PROSPECTS ---------------------------- */
function MyProspects() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [minScore, setMinScore] = useState(0);
  const [scope, setScope] = useState("mine");
  const [openId, setOpenId] = useState(null);
  const load = useCallback(async () => {
    try { setRows((await api.get(`/intelligence/prospects?scope=${scope}&min_score=${minScore}`)).data.items); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [scope, minScore]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="intel-mine">
      {openId && <ProspectDrawer partyId={openId} onClose={() => { setOpenId(null); load(); }} />}
      <div className="flex items-center gap-3 flex-wrap">
        {can.admin && (
          <div className="flex gap-1">
            {["mine", "all"].map((s) => <button key={s} data-testid={`intel-scope-${s}`} onClick={() => setScope(s)} className={`px-2.5 py-1 text-xs rounded-sm border ${scope === s ? "border-atlantic bg-atlantic/10 text-navy" : "border-border text-mutedsea"}`}>{s === "mine" ? "My prospects" : "All producers"}</button>)}
          </div>
        )}
        <div className="flex items-center gap-2 text-xs text-mutedsea">Min score
          <select value={minScore} onChange={(e) => setMinScore(Number(e.target.value))} className="border border-border rounded-sm px-2 py-1 bg-white" data-testid="intel-minscore">
            {[0, 40, 60, 75, 90].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cloud text-mutedsea text-xs uppercase"><tr>
            <th className="text-left px-3 py-2">Company</th><th className="text-left px-3 py-2">Score</th>
            <th className="text-left px-3 py-2">Location</th><th className="text-left px-3 py-2">Industry</th>
            <th className="text-left px-3 py-2">Potential lines</th><th className="text-left px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {rows == null && <tr><td colSpan={6} className="px-3 py-8 text-center text-mutedsea">Loading…</td></tr>}
            {rows?.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-mutedsea">No prospects yet.</td></tr>}
            {rows?.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-cloud/40" data-testid={`intel-prospect-${r.id}`}>
                <td className="px-3 py-2"><a href={`/clients/${r.id}`} className="text-navy font-medium hover:text-atlantic">{r.display_name}</a></td>
                <td className="px-3 py-2"><Badge variant="outline" className={scoreColor(r.score)}>{r.score} · {r.priority_label}</Badge></td>
                <td className="px-3 py-2 text-slate">{[r.address_city, r.address_state].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-3 py-2 text-slate">{r.industry || "—"}</td>
                <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{(r.suggested_lines || []).slice(0, 3).map((l) => <Badge key={l} variant="outline" className="bg-cloud text-mutedsea border-border text-[10px]">{l}</Badge>)}</div></td>
                <td className="px-3 py-2"><button onClick={() => setOpenId(r.id)} className="text-xs text-atlantic hover:text-navy" data-testid={`intel-open-drawer-${r.id}`}>Decision makers</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------- PROSPECT DRAWER (decision makers) --------------------- */
function ProspectDrawer({ partyId, onClose }) {
  const [data, setData] = useState(null);
  const [people, setPeople] = useState(null);
  const [cats, setCats] = useState(["Owner / Executive", "Financial Decision Maker", "Operations", "Risk / Insurance"]);
  const [searching, setSearching] = useState(false);
  const [brief, setBrief] = useState(null);
  const [briefBusy, setBriefBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const load = useCallback(async () => {
    try { const { data: d } = await api.get(`/intelligence/prospects/${partyId}`); setData(d); setBrief(d.brief || null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [partyId]);
  useEffect(() => { load(); }, [load]);

  const findPeople = async () => {
    setSearching(true);
    try {
      const { data: d } = await api.post(`/intelligence/prospects/${partyId}/people-search`, { categories: cats, per_page: 10 });
      setPeople(d.people);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSearching(false); }
  };
  const addContact = async (person) => {
    try {
      const { data: d } = await api.post(`/intelligence/prospects/${partyId}/contacts`, { person });
      d.result === "created" ? toast.success(`Added ${person.name}`) : toast.info(`${person.name} already added`);
      setPeople((ps) => ps.map((p) => p.apollo_person_id === person.apollo_person_id ? { ...p, already_added: true } : p));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reveal = async (contact, email, phone) => {
    if (phone) {
      const { data: est } = await api.post("/intelligence/enrich/estimate", { count: 1, reveal_email: email, reveal_phone: phone });
      if (!window.confirm(`This will enrich 1 contact using Apollo (~${est.estimated_credits} credits). Continue?`)) return;
    }
    try {
      const { data: d } = await api.post(`/intelligence/contacts/${contact.id}/reveal`, { reveal_email: email, reveal_phone: phone });
      if (d.phone_status === "pending") toast.info("Phone lookup in progress — it will appear shortly.");
      else toast.success("Contact details revealed.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const genBrief = async () => {
    setBriefBusy(true);
    try { const { data: d } = await api.post(`/intelligence/prospects/${partyId}/brief`); setBrief(d.brief); toast.success("Insurance brief generated."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBriefBusy(false); }
  };
  const genDraft = async (contactId) => {
    setDraftBusy(true);
    try { const { data: d } = await api.post(`/intelligence/prospects/${partyId}/draft-outreach`, { contact_id: contactId || "" }); setDraft(d); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDraftBusy(false); }
  };
  const sendDraft = async () => {
    if (!draft.to_email) { toast.error("Enter or reveal a recipient email first."); return; }
    if (!window.confirm(`Send this approved outreach to ${draft.to_email}?`)) return;
    try {
      await api.post(`/intelligence/outreach/${draft.id}/send`, { to_email: draft.to_email, subject: draft.subject, body: draft.body });
      toast.success("Outreach sent via Proton and logged to chronology.");
      setDraft(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const p = data?.party || {};
  const intel = data?.intel || {};
  const contacts = data?.contacts || [];
  const createOpp = async () => {
    try { const { data: d } = await api.post(`/intelligence/prospects/${partyId}/create-opportunity`, {}); toast.success(`Opportunity ${d.opp_ref} created.`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setOutcome = async (state) => {
    if (!state) return;
    try { await api.post(`/intelligence/prospects/${partyId}/outcome`, { state }); toast.success(`Marked ${state}.`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="intel-drawer">
      <div className="absolute inset-0 bg-deepnavy/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-cloud h-full overflow-y-auto shadow-2xl p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-serif text-2xl text-navy">{p.display_name}</div>
            <div className="text-xs text-mutedsea">{p.industry || intel.industry} · {[p.address_city, p.address_state].filter(Boolean).join(", ")}</div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={scoreColor(intel.score)}>{intel.score} · {intel.priority_label}</Badge>
            <button onClick={onClose} className="text-mutedsea hover:text-navy text-sm" data-testid="intel-drawer-close">✕</button>
          </div>
        </div>

        {intel.score_breakdown?.length > 0 && (
          <div className="bg-white border border-border rounded-sm p-3 mt-4" data-testid="intel-why-score">
            <div className="text-xs font-medium text-slate mb-1">Why this score</div>
            <div className="space-y-0.5">
              {intel.score_breakdown.map((f, i) => (
                <div key={i} className="flex justify-between text-xs"><span className="text-mutedsea">{f.factor} — {f.note} <span className="text-[10px] opacity-60">({f.source})</span></span><span className="text-navy font-medium">{f.points}/{f.weight}</span></div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {p.prospect_state === "OPPORTUNITY CREATED" || intel.opportunity_id
            ? <Badge variant="outline" className="bg-seagreen/15 text-seagreen border-seagreen/30" data-testid="intel-opp-created">Opportunity created</Badge>
            : <Button size="sm" onClick={createOpp} className="bg-navy hover:bg-atlantic text-white h-8" data-testid="intel-create-opp"><ChevronRight className="h-4 w-4 mr-1" />Create Opportunity</Button>}
          <select defaultValue="" onChange={(e) => setOutcome(e.target.value)} className="border border-border rounded-sm px-2 py-1.5 text-xs bg-white" data-testid="intel-outcome">
            <option value="">Set outcome…</option>
            {["QUALIFIED", "ENGAGED", "NURTURE", "DISQUALIFIED", "SUPPRESSED"].map((s) => <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>)}
          </select>
          {p.prospect_state && <Badge variant="outline" className="bg-cloud text-mutedsea border-border text-[10px]">{p.prospect_state}</Badge>}
        </div>

        <div className="bg-white border border-border rounded-sm p-3 mt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-serif text-lg text-navy flex items-center gap-2"><Users className="h-4 w-4 text-atlantic" /> Decision Makers</div>
            <Button size="sm" onClick={findPeople} disabled={searching} className="bg-navy hover:bg-atlantic text-white h-8" data-testid="intel-find-people"><Search className="h-4 w-4 mr-1.5" />{searching ? "Searching…" : "Find Decision Makers"}</Button>
          </div>
          <div className="text-[11px] text-mutedsea mb-2">People search is free — contact details are only revealed on request.</div>

          {contacts.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-medium text-slate mb-1">Saved contacts</div>
              {contacts.map((c) => (
                <div key={c.id} className="border border-border rounded-sm p-2 mb-1.5 text-sm" data-testid={`intel-contact-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <div><span className="text-navy font-medium">{c.name}</span> <span className="text-xs text-mutedsea">{c.title} · {c.dm_category}</span></div>
                    <div className="flex gap-1.5">
                      {c.email ? <span className="text-xs text-seagreen">{c.email}</span>
                        : <Button size="sm" variant="outline" className="h-6 text-[11px]" data-testid={`intel-reveal-email-${c.id}`} onClick={() => reveal(c, true, false)}>Reveal Email</Button>}
                      {c.mobile_phone ? <span className="text-xs text-seagreen">{c.mobile_phone}</span>
                        : c.phone_status === "pending" ? <span className="text-xs text-seaamber">Phone…</span>
                        : <Button size="sm" variant="outline" className="h-6 text-[11px]" data-testid={`intel-reveal-phone-${c.id}`} onClick={() => reveal(c, false, true)}>Reveal Phone</Button>}
                    </div>
                  </div>
                  {c.email_status === "unavailable" && !c.email && <div className="text-[10px] text-mutedsea">Email not available from Apollo</div>}
                </div>
              ))}
            </div>
          )}

          {people && (
            <div>
              <div className="text-xs font-medium text-slate mb-1">Apollo results</div>
              {people.length === 0 && <div className="text-xs text-mutedsea py-2">No decision makers found for the selected roles.</div>}
              {people.map((pp) => (
                <div key={pp.apollo_person_id} className="flex items-center justify-between border-b border-border py-1.5 text-sm" data-testid={`intel-person-${pp.apollo_person_id}`}>
                  <div><span className="text-navy">{pp.name}</span> <span className="text-xs text-mutedsea">{pp.title} · {pp.dm_category}</span></div>
                  {pp.already_added ? <span className="text-xs text-mutedsea">Added</span>
                    : <Button size="sm" variant="outline" className="h-6 text-[11px]" data-testid={`intel-add-contact-${pp.apollo_person_id}`} onClick={() => addContact(pp)}>Add Contact</Button>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Insurance Research Brief */}
        <div className="bg-white border border-border rounded-sm p-3 mt-3" data-testid="intel-brief-section">
          <div className="flex items-center justify-between mb-2">
            <div className="font-serif text-lg text-navy flex items-center gap-2"><Sparkles className="h-4 w-4 text-atlantic" /> Insurance Research Brief</div>
            <Button size="sm" onClick={genBrief} disabled={briefBusy} className="bg-navy hover:bg-atlantic text-white h-8" data-testid="intel-gen-brief">{briefBusy ? "Generating…" : (brief ? "Regenerate" : "Generate Brief")}</Button>
          </div>
          {!brief ? <div className="text-xs text-mutedsea">Generate a factual brief (known vs inferred — never fabricates insurance facts).</div> : (
            <div className="space-y-2 text-sm" data-testid="intel-brief-body">
              {brief.snapshot && <p className="text-slate">{brief.snapshot}</p>}
              {brief.known_facts?.length > 0 && <div><div className="text-xs font-medium text-seagreen">Known</div><ul className="text-xs text-slate list-disc ml-4">{brief.known_facts.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
              {brief.inferred?.length > 0 && <div><div className="text-xs font-medium text-seaamber">Inferred (not verified)</div><ul className="text-xs text-mutedsea list-disc ml-4">{brief.inferred.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
              {brief.opportunities?.length > 0 && <div><div className="text-xs font-medium text-atlantic">Potential opportunities</div><div className="flex flex-wrap gap-1">{brief.opportunities.map((x, i) => <Badge key={i} variant="outline" className="bg-cloud text-mutedsea border-border text-[10px]">{x}</Badge>)}</div></div>}
              {brief.next_action && <div className="text-xs"><span className="font-medium text-slate">Next action: </span><span className="text-mutedsea">{brief.next_action}</span></div>}
            </div>
          )}
        </div>

        {/* AI Outreach */}
        <div className="bg-white border border-border rounded-sm p-3 mt-3 mb-4" data-testid="intel-outreach-section">
          <div className="flex items-center justify-between mb-2">
            <div className="font-serif text-lg text-navy flex items-center gap-2"><Sparkles className="h-4 w-4 text-atlantic" /> Outreach</div>
            <Button size="sm" onClick={() => genDraft(contacts[0]?.id)} disabled={draftBusy} variant="outline" className="h-8" data-testid="intel-draft-outreach">{draftBusy ? "Drafting…" : "Draft with AI"}</Button>
          </div>
          {!draft ? <div className="text-xs text-mutedsea">Draft a personalized first-touch email from verified data. You approve before anything is sent.</div> : (
            <div className="space-y-2" data-testid="intel-draft-editor">
              <div><Label className="text-xs mb-1">To</Label><Input value={draft.to_email} onChange={(e) => setDraft({ ...draft, to_email: e.target.value })} placeholder="recipient@company.com" className="bg-white h-8 text-sm" data-testid="intel-draft-to" /></div>
              <div><Label className="text-xs mb-1">Subject</Label><Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="bg-white h-8 text-sm" data-testid="intel-draft-subject" /></div>
              <div><Label className="text-xs mb-1">Body</Label><textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={7} className="w-full border border-border rounded-sm p-2 text-sm bg-white" data-testid="intel-draft-body" /></div>
              <div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={() => setDraft(null)}>Discard</Button><Button size="sm" onClick={sendDraft} className="bg-navy hover:bg-atlantic text-white" data-testid="intel-send-outreach">Approve & Send</Button></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ SETTINGS ------------------------------ */
function IntelSettings() {
  const { can } = useAuth();
  const [status, setStatus] = useState(null);
  const [usage, setUsage] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [icps, setIcps] = useState([]);
  const [testing, setTesting] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [s, u, c, i] = await Promise.all([
        api.get("/intelligence/apollo/status"), api.get("/intelligence/apollo/usage"),
        api.get("/intelligence/config"), api.get("/intelligence/icps"),
      ]);
      setStatus(s.data); setUsage(u.data); setCfg(c.data); setIcps(i.data.items);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const testConn = async () => {
    setTesting(true);
    try { const { data } = await api.post("/intelligence/apollo/test"); data.ok ? toast.success(data.message) : toast.error(data.message); await loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };
  const saveCfg = async (patch) => {
    try { const { data } = await api.put("/intelligence/config", patch); setCfg(data); toast.success("Settings saved."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!status) return <div className="text-mutedsea text-sm py-10 text-center">Loading settings…</div>;
  const stateColor = status.state === "connected" ? "bg-seagreen/15 text-seagreen border-seagreen/30" : status.state === "not_configured" ? "bg-cloud text-mutedsea border-border" : "bg-seared/15 text-seared border-seared/30";
  return (
    <div className="space-y-4" data-testid="intel-settings">
      <div className="bg-white border border-border rounded-sm p-4">
        <div className="font-serif text-lg text-navy mb-3 flex items-center gap-2"><Plug className="h-4 w-4 text-atlantic" /> Data Providers · Apollo</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><div className="text-xs text-mutedsea">Connection</div><Badge variant="outline" className={`${stateColor} mt-1`} data-testid="intel-apollo-state">{status.state}</Badge></div>
          <div><div className="text-xs text-mutedsea">API key configured</div><div className="text-navy mt-1">{status.api_key_configured ? "Yes" : "No"}</div></div>
          <div><div className="text-xs text-mutedsea">Last success</div><div className="text-slate mt-1">{status.last_success?.at ? new Date(status.last_success.at).toLocaleString() : "—"}</div></div>
          <div><div className="text-xs text-mutedsea">Last failure</div><div className="text-slate mt-1">{status.last_failure?.at ? new Date(status.last_failure.at).toLocaleString() : "—"}</div></div>
        </div>
        <p className="text-xs text-mutedsea mt-3">{status.message}</p>
        {can.admin && <div className="mt-3"><Button size="sm" onClick={testConn} disabled={testing} data-testid="intel-test-connection" className="bg-navy hover:bg-atlantic text-white"><RefreshCw className="h-4 w-4 mr-1.5" />{testing ? "Testing…" : "Test Connection"}</Button></div>}
      </div>

      <div className="bg-white border border-border rounded-sm p-4">
        <div className="font-serif text-lg text-navy mb-3 flex items-center gap-2"><Gauge className="h-4 w-4 text-atlantic" /> Usage Today</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          {[["org_searches", "Searches"], ["org_enrichments", "Company Enrichments"], ["people_enrichments", "People Enrichments"], ["rate_limited_events", "Rate-Limit Events"], ["api_failures", "API Failures"]].map(([k, l]) => (
            <div key={k}><div className="font-serif text-2xl text-navy">{usage?.today?.[k] ?? 0}</div><div className="text-xs text-mutedsea">{l}</div></div>
          ))}
        </div>
      </div>

      {cfg && can.admin && (
        <div className="bg-white border border-border rounded-sm p-4">
          <div className="font-serif text-lg text-navy mb-3">Cost & Credit Controls</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[["daily_search_cap", "Daily search cap"], ["max_org_enrich_per_day", "Max company enrich/day"], ["max_people_enrich_per_day", "Max people enrich/day"], ["require_approval_above", "Approval above N contacts"], ["monthly_credit_threshold", "Monthly credit threshold"], ["warning_threshold", "Warning threshold"]].map(([k, l]) => (
              <div key={k}><Label className="text-xs mb-1">{l}</Label><Input type="number" defaultValue={cfg[k]} onBlur={(e) => Number(e.target.value) !== cfg[k] && saveCfg({ [k]: Number(e.target.value) })} className="bg-white h-9 text-sm" data-testid={`intel-cfg-${k}`} /></div>
            ))}
          </div>
          <div className="flex gap-4 mt-3">
            {[["allow_email_enrichment", "Allow email enrichment"], ["allow_phone_enrichment", "Allow phone enrichment"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate"><input type="checkbox" checked={!!cfg[k]} onChange={(e) => saveCfg({ [k]: e.target.checked })} data-testid={`intel-cfg-${k}`} />{l}</label>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-border rounded-sm p-4">
        <div className="font-serif text-lg text-navy mb-3 flex items-center gap-2"><Target className="h-4 w-4 text-atlantic" /> Ideal Customer Profiles</div>
        {icps.length === 0 ? <p className="text-sm text-mutedsea">No ICPs yet. Scoring uses default weights until you add one.</p> : (
          <div className="space-y-1.5">
            {icps.map((i) => (
              <div key={i.id} className="flex items-center justify-between text-sm border border-border rounded-sm px-3 py-2" data-testid={`intel-icp-${i.id}`}>
                <div><span className="text-navy font-medium">{i.name}</span> <span className="text-xs text-mutedsea">· {(i.states || []).join(", ") || "any state"} · {(i.employee_range?.min ?? "?")}–{(i.employee_range?.max ?? "?")} emp</span></div>
                <Badge variant="outline" className={i.active ? "bg-seagreen/15 text-seagreen border-seagreen/30" : "bg-cloud text-mutedsea border-border"}>{i.active ? "Active" : "Inactive"}</Badge>
              </div>
            ))}
          </div>
        )}
        {can.admin && <IcpCreator onCreated={loadAll} />}
      </div>
    </div>
  );
}

function IcpCreator({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", states: "", emin: "", emax: "", rmin: "", rmax: "", industries: "", lines: "" });
  const save = async () => {
    if (!f.name) { toast.error("Name is required."); return; }
    try {
      await api.post("/intelligence/icps", {
        name: f.name, states: f.states.split(",").map((s) => s.trim()).filter(Boolean),
        employee_range: { min: f.emin ? Number(f.emin) : null, max: f.emax ? Number(f.emax) : null },
        revenue_range: { min: f.rmin ? Number(f.rmin) : null, max: f.rmax ? Number(f.rmax) : null },
        industries: f.industries.split(",").map((s) => s.trim()).filter(Boolean),
        target_lines: f.lines.split(",").map((s) => s.trim()).filter(Boolean), active: true, priority: 1,
      });
      toast.success("ICP created."); setOpen(false); setF({ name: "", states: "", emin: "", emax: "", rmin: "", rmax: "", industries: "", lines: "" }); onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!open) return <Button size="sm" variant="outline" className="mt-3" onClick={() => setOpen(true)} data-testid="intel-icp-add">Add ICP</Button>;
  return (
    <div className="mt-3 border border-border rounded-sm p-3 bg-cloud/40 space-y-2" data-testid="intel-icp-form">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[["name", "Name"], ["states", "States (NY,NJ)"], ["emin", "Min employees"], ["emax", "Max employees"], ["rmin", "Min revenue"], ["rmax", "Max revenue"], ["industries", "Industries"], ["lines", "Target lines"]].map(([k, l]) => (
          <div key={k}><Label className="text-xs mb-1">{l}</Label><Input value={f[k]} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} className="bg-white h-8 text-sm" data-testid={`intel-icp-${k}`} /></div>
        ))}
      </div>
      <div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button size="sm" onClick={save} className="bg-navy hover:bg-atlantic text-white" data-testid="intel-icp-save">Save ICP</Button></div>
    </div>
  );
}
